import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import Hls from "hls.js";
import { useQueryClient } from "react-query";

import { Video, VideoFile } from "@/api";
import {
  EVENT_TYPE_WATCHED,
  QUERY_COURSE_ID,
  QUERY_PARENT_ID,
  VIDEO_TYPE_AUDIO,
} from "@/const.ts";
import { convertSrtToVtt, getCurrentDateTime, isLiveVideo } from "@/utils";
import { useAppProperties, usePutEvent } from "@/hooks";
import { useLivestream } from "../hooks/useLivestream.tsx";

import { CustomVideoPlayer } from "./CustomPlayer/CustomVideoPlayer.tsx";
import { CustomAudioPlayer } from "./CustomPlayer/CustomAudioPlayer.tsx";
import { useProgressTracking } from "../hooks/useProgressTracking.tsx";
import { VideoAutoPlay } from "@/app/modules/ContentDetails/components/VideoAutoPlay.tsx";
import { isAutoplayEnabled } from "@/utils/localStorage.ts";
import toast from "react-hot-toast";
import { CompleteToast } from "./CompleteToast.tsx";
import { COMPLETE_TOAST_CONFIG } from "./const.ts";

export interface VideoQualityOptions {
  name: string;
  value: number;
}

interface HlsPlayerProps {
  video: Video;
  src: string;
  autoPlay?: boolean;
  subtitles?: VideoFile[];
}

export const HLSVideoPlayer = ({
  src,
  video,
  autoPlay,
  subtitles,
}: HlsPlayerProps) => {
  const queryClient = useQueryClient();

  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const hasPlayedRef = useRef<boolean>(false);
  const [query] = useSearchParams();
  const parentId = query.get(QUERY_PARENT_ID);
  const courseId = query.get(QUERY_COURSE_ID);

  // playing is only manually controlled for Custom Audio player at present
  const [playing, setPlaying] = useState(false);
  const [autoplaying, setAutoplaying] = useState(false);
  const [qualities, setQualities] = useState<VideoQualityOptions[]>([]);
  const [currentQuality, setCurrentQuality] = useState<number>(-1);
  const [subtitleTracks, setSubtitleTracks] = useState<HTMLTrackElement[]>([]);

  const { showCalendar, completedMessage, subtitleDisabled } =
    useAppProperties();

  const { isLiveEnded, liveStartDate } = useLivestream({
    video,
    videoId: video.VideoId,
  });
  const { sendProgress, startProgress, stopProgress } = useProgressTracking({
    video,
  });

  const { mutate: putEventMutate } = usePutEvent();
  const completedRef = useRef(false); // Ref to track completed status

  const handleVideoProgress = useCallback(
    (player: HTMLVideoElement) => {
      if (isLiveVideo({ Properties: video.Properties })) {
        return;
      }
      const percentagePlayed = (player.currentTime / player.duration) * 100;
      if (percentagePlayed >= 90 && !completedRef.current) {
        // Inside this setter gets the value of completed in realtime
        putEventMutate({
          ItemId: video.VideoId,
          ItemType: "video",
          SourceId: video.SourceId,
          DataSource: video.DataSource,
          EventDate: getCurrentDateTime(),
          EventType: EVENT_TYPE_WATCHED,
          ItemParentId: parentId ? parseInt(parentId) : undefined,
          ItemCourseId: courseId ? parseInt(courseId) : undefined,
        });
        completedRef.current = true;
      }
    },
    [
      video.Properties,
      video.VideoId,
      video.SourceId,
      video.DataSource,
      putEventMutate,
      parentId,
      courseId,
    ],
  );

  const handleVideoPlay = useCallback(
    (player: HTMLVideoElement) => {
      // Send initial event on starting the video for the first time
      if (!hasPlayedRef.current) {
        hasPlayedRef.current = true;
        sendProgress(player);
      }

      // Start adding progress tracking every minute
      startProgress(player);
    },
    [sendProgress, startProgress],
  );

  const handleLiveStreamEnded = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["videofiles", video.SourceId, video.DataSource],
    });
  }, [video.SourceId, video.DataSource, queryClient]);

  const handleVideoEnded = useCallback(
    async (player: HTMLVideoElement) => {
      // Send the final progress tracking event and end the tracking
      sendProgress(player);
      stopProgress();

      // When a livestream stops, invalidate the videofiles query to get the latest livestream status and show stream ended overlay
      if (liveStartDate && !isLiveEnded) {
        handleLiveStreamEnded();
      }

      if (showCalendar) {
        // Congratulate user for finishing the video
        toast(
          <CompleteToast message={completedMessage} />,
          COMPLETE_TOAST_CONFIG,
        );
      }
      if (isAutoplayEnabled()) {
        setAutoplaying(true);
      }
    },
    [
      completedMessage,
      stopProgress,
      sendProgress,
      showCalendar,
      isLiveEnded,
      liveStartDate,
      handleLiveStreamEnded,
    ],
  );
  useEffect(() => {
    if (subtitleDisabled) {
      return;
    }

    const blobUrls: string[] = [];
    const tracksList: HTMLTrackElement[] = [];

    const addSubtitleTracks = async () => {
      if (!subtitles?.length) {
        return;
      }

      for (let i = 0; i < subtitles.length; i++) {
        const {
          URL: Url,
          LanguageDisplayName,
          LanguageCode,
          Type,
        } = subtitles[i];
        try {
          const response = await fetch(Url);
          let subtitleContent = await response.text();

          // Convert SRT to VTT
          if (Type.toUpperCase() === "SRT") {
            subtitleContent = convertSrtToVtt(subtitleContent);
          }

          const blob = new Blob([subtitleContent], { type: "text/vtt" });
          const blobUrl = URL.createObjectURL(blob);
          blobUrls.push(blobUrl);

          const trackElem = document.createElement("track");

          const label = LanguageDisplayName ?? `Subtitles ${i + 1}`;
          trackElem.label = label;
          trackElem.srclang = LanguageCode ?? "unknown";
          trackElem.src = blobUrl;
          trackElem.id = `track-${i}-${label}`;

          tracksList.push(trackElem);
        } catch (error) {
          console.error("Error loading subtitle:", error);
        }
      }
      setSubtitleTracks(tracksList);
    };

    addSubtitleTracks();

    return () => {
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [subtitles, subtitleDisabled]);

  useEffect(() => {
    let hls: Hls;
    const player = videoRef?.current;
    const handlePlay = player ? () => handleVideoPlay(player) : undefined;
    const handleTimeUpdate = player
      ? () => handleVideoProgress(player)
      : undefined;
    const handleEnded = player ? () => handleVideoEnded(player) : undefined;

    if (player) {
      if (Hls.isSupported()) {
        hls = new Hls();
        hls.loadSource(src);
        hls.attachMedia(player);

        hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
          const availableQualities = data.levels.map((level, index) => ({
            name: `${level.height.toString()}p`,
            value: index,
          }));
          setQualities(availableQualities);
          setCurrentQuality(hls.currentLevel);
        });

        hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
          setCurrentQuality(data.level);
        });
        // Store hls instance in ref for quality changes
        hlsRef.current = hls;
      } else if (player.canPlayType("application/vnd.apple.mpegurl")) {
        // This will run on browsers like Safari, which support HLS natively
        player.src = src;
      }
      if (handlePlay) {
        player.addEventListener("play", handlePlay);
      }
      if (handleTimeUpdate) {
        player.addEventListener("timeupdate", handleTimeUpdate);
      }
      if (handleEnded) {
        player.addEventListener("ended", handleEnded);
      }
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
      if (player) {
        if (handlePlay) {
          player.removeEventListener("play", handlePlay);
        }
        if (handleTimeUpdate) {
          player.removeEventListener("timeupdate", handleTimeUpdate);
        }
        if (handleEnded) {
          player.removeEventListener("ended", handleEnded);
        }
      }
    };
  }, [src, handleVideoProgress, handleVideoPlay, handleVideoEnded]);

  const handleQualityChange = useCallback((level: number) => {
    if (hlsRef.current) {
      hlsRef.current.currentLevel = level;
    }
  }, []);

  const handleReplay = () => {
    setAutoplaying(false);
    setPlaying(true);
  };

  const playerProps = useMemo(
    () => ({
      ref: videoRef,
      autoPlay,
      playing,
      setPlaying,
    }),
    [videoRef, autoPlay, playing, setPlaying],
  );

  const autoplayProps = useMemo(
    () => ({
      itemId: video.VideoId,
      type: video.Type,
      onCancel: () => setAutoplaying(false),
      onReplay: handleReplay,
      startCountdownExternally: autoplaying,
    }),
    [video.VideoId, video.Type, autoplaying],
  );

  return (
    <div className="relative">
      {video.Type === VIDEO_TYPE_AUDIO ? (
        <CustomAudioPlayer {...playerProps} />
      ) : (
        <CustomVideoPlayer
          thumbnail={video.Thumbnails.large ?? video.Thumbnails.source}
          itemId={video.VideoId}
          qualities={qualities}
          currentQuality={currentQuality}
          onQualityChange={handleQualityChange}
          subtitleTracks={subtitleTracks}
          handleReplay={handleReplay}
          isLive={!!liveStartDate && !isLiveEnded}
          {...playerProps}
        />
      )}
      {autoplaying && (
        <>
          {video.Type === VIDEO_TYPE_AUDIO ? (
            <VideoAutoPlay {...autoplayProps} />
          ) : (
            createPortal(
              <VideoAutoPlay {...autoplayProps} />,
              document.getElementById(
                "fullscreen-player-portal",
              ) as HTMLElement,
            )
          )}
        </>
      )}
    </div>
  );
};
